import React, { useState, useRef } from "react";
import toast from "react-hot-toast";
import { ThumbsUp, ThumbsDown, MessageSquare } from "lucide-react";
import { humanFeedbackService } from "../../apis/humanFeedbackService";

export default function FeedbackModal({
  isOpen,
  section,
  type,
  content,
  contentType,
  setFeedbackModal,
  soapNoteId,
  setFeedbackSubmitted,
  onFeedbackSubmitted,
}) {
  const [feedbackText, setFeedbackText] = useState("");
  const feedbackTextLength = 3000;

  // --- For dragging ---
  const modalRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialPos, setInitialPos] = useState({ x: 0, y: 0 });

  const onMouseDown = (e) => {
    if (e.target.closest(".drag-handle")) {
      setDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
      setInitialPos({ x: position.x, y: position.y });
      e.stopPropagation();
      e.preventDefault();
    }
  };

  const onMouseMove = (e) => {
    if (dragging) {
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      setPosition({ x: initialPos.x + dx, y: initialPos.y + dy });
    }
  };

  const onMouseUp = () => {
    setDragging(false);
  };

  React.useEffect(() => {
    if (dragging) {
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    } else {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [dragging]);

  if (!isOpen) return null;

  const onClose = () => {
    setFeedbackModal({
      isOpen: false,
      section: "",
      type: "",
      content: "",
      contentType: "",
    });
    setFeedbackText("");
  };

  const submitFeedback = async () => {
    try {
      if (feedbackText.length > feedbackTextLength) {
        toast.error(`Feedback exceeds ${feedbackTextLength} characters limit!`);
        return;
      }
      if (!feedbackText.trim()) {
        toast.error("Feedback cannot be empty!");
        return;
      }
      const rating = type === "like" ? 1 : -1;
      const isGlobalFeedback =
        contentType === "entire-section" || !content || content.length === 0;
      const feedbackSection =
        section === "assessment" || section === "plan"
          ? "assessmentAndPlan"
          : section;

      const feedbackData = {
        soapNoteId,
        section: feedbackSection,
        rating,
        feedbackText,
        feedbackType: isGlobalFeedback ? "global-level" : "paragraph-level",
        reportedContent: isGlobalFeedback ? "" : content,
        contentType:
          contentType || (isGlobalFeedback ? "entire-section" : "unknown"),
        timestamp: new Date().toISOString(),
      };

      await humanFeedbackService.submitHumanFeedbackOnAIGeneratedSoapNote(
        soapNoteId,
        feedbackSection,
        rating,
        feedbackText,
        feedbackData
      );

      toast.success("Feedback submitted successfully!");
      setFeedbackSubmitted((prev) => ({ ...prev, [section]: type }));
      if (onFeedbackSubmitted) {
        onFeedbackSubmitted(
          section,
          type,
          feedbackData.feedbackType,
          feedbackData.reportedContent
        );
      }
      setFeedbackText("");
      onClose();
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong!");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4 pointer-events-none">
      <div
        ref={modalRef}
        onMouseDown={onMouseDown}
        style={{
          transform: `translate(${position.x}px, ${position.y}px)`,
          cursor: dragging ? "grabbing" : "default",
        }}
        className="bg-white rounded-2xl shadow-xl p-5 w-full max-w-lg relative pointer-events-auto"
      >
        {/* Draggable Handle anywhere */}
        <div className="drag-handle absolute top-0 left-0 right-0 cursor-grab active:cursor-grabbing bg-gray-100 text-gray-600 text-center py-2 rounded-t-2xl font-medium">
          Drag me anywhere ↕️
        </div>

        <div className="mt-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">
            Send Note Feedback to VPS Team
          </h2>

          {(() => {
            const isGlobalFeedback =
              contentType === "entire-section" ||
              !content ||
              content.length === 0;

            // Define dynamic color and icon sets for thumbs up/down/
            const colorSet =
              type === "dislike"
                ? {
                  bg: "bg-red-50",
                  border: "border-red-300",
                  accent: "border-l-4 border-l-red-500",
                  textMain: "text-red-800",
                  textAccent: "text-red-600",
                  badgeBg: "bg-red-100",
                  emoji: "👎",
                }
                : type === "like"
                  ? {
                    bg: "bg-green-50",
                    border: "border-green-300",
                    accent: "border-l-4 border-l-green-500",
                    textMain: "text-green-800",
                    textAccent: "text-green-600",
                    badgeBg: "bg-green-100",
                    emoji: "👍",
                  }
                  : {
                    bg: "bg-blue-50",
                    border: "border-blue-300",
                    accent: "border-l-4 border-l-blue-500",
                    textMain: "text-blue-800",
                    textAccent: "text-blue-600",
                    badgeBg: "bg-blue-100",
                    emoji: "💬",
                  };

            if (isGlobalFeedback) {
              return (
                <div
                  className={`mb-4 p-3 ${colorSet.bg} ${colorSet.border} ${colorSet.accent} rounded-lg shadow-sm transition-all`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span
                      className={`text-sm font-medium flex items-center gap-2 ${colorSet.textMain}`}
                    >
                      <span className="text-lg">{colorSet.emoji}</span>
                      Reporting Global Feedback
                    </span>
                    <span
                      className={`text-xs ${colorSet.textAccent} ${colorSet.badgeBg} px-2 py-1 rounded-full`}
                    >
                      {section}
                    </span>
                  </div>
                  <div className={`text-sm ${colorSet.textAccent}`}>
                    Your feedback will be recorded at the global level for the
                    entire <b>{section}</b> section.
                  </div>
                </div>
              );
            } else {
              return (
                <div
                  className={`mb-4 p-3 ${colorSet.bg} ${colorSet.border} ${colorSet.accent} rounded-lg shadow-sm transition-all`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span
                      className={`text-sm font-medium flex items-center gap-2 ${colorSet.textMain}`}
                    >
                      <span className="text-lg">{colorSet.emoji}</span>
                      Reporting{" "}
                      {contentType === "bullet-point"
                        ? "Bullet Point"
                        : "Paragraph"}{" "}
                      Content
                    </span>
                    <span
                      className={`text-xs ${colorSet.textAccent} ${colorSet.badgeBg} px-2 py-1 rounded-full`}
                    >
                      {section}
                    </span>
                  </div>
                  <div
                    className={`text-sm ${colorSet.textAccent} bg-white p-2 rounded border max-h-20 overflow-y-auto`}
                  >
                    {content.length > 200
                      ? `${content.substring(0, 200)}...`
                      : content}
                  </div>
                </div>
              );
            }
          })()}

          <textarea
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            placeholder={`Write your feedback (max ${feedbackTextLength} chars)...`}
            className="w-full border border-gray-300 rounded-lg p-3 min-h-[150px] max-h-[300px] text-gray-700 focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
          />
          <p className="text-gray-400">
            Please avoid personal health information.
          </p>
          <div className="text-sm text-gray-500 mt-1 text-right">
            <span
              className={
                feedbackText.length > feedbackTextLength
                  ? "text-red-500 font-semibold"
                  : ""
              }
            >
              {feedbackText.length}
            </span>
            &nbsp;/ {feedbackTextLength}
          </div>

          <div className="flex justify-end mt-4 gap-3">
            <button
              onClick={() => {
                setFeedbackText("");
                onClose();
              }}
              className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm sm:text-base"
            >
              Cancel
            </button>

            <button
              onClick={submitFeedback}
              disabled={!feedbackText.trim()}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 text-sm sm:text-base"
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
